// ==============================
// Imports
// ==============================

// MUI Components
import { SelectChangeEvent } from '@mui/material/Select';

// Local libraries
import React, { useCallback, useEffect, useState } from 'react';

// Local Components
import { apiDelete, apiGet } from '../utils/apiClient';
import { PaginatedResponse, User } from '../types/UserTable.type';
import { notifyAPIClient } from '../utils/toast';
import { SuccessResponse } from '../types/CourseStageAccordion.type';
import dayjs from 'dayjs';
import { exportRowsToXlsx } from '../utils/xlsxExport';
// import { notifyActionSuccess } from '../utils/toast';

// Types

export const useUserController = () => {
  // States
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('All');
  // Duplicate-account filters — surface merge candidates on the /client screen.
  const [duplicatePhoneFilter, setDuplicatePhoneFilter] = useState(false);
  const [duplicateEmailFilter, setDuplicateEmailFilter] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [openUserInfoDialog, setOpenUserInfoDialog] = useState(false);
  const [openDeleteUserDialog, setOpenDeleteUserDialog] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  // const [orderBy, setOrderBy] = useState<keyof User>("id");
  // const [order, setOrder] = useState<Order>("asc");

  // Last sync time for users
  const [lastSyncTime, setLastSyncTime] = useState<dayjs.Dayjs | null>(null);

  // Highlighted row
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);

  // Networking
  const fetchUsers = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiGet<PaginatedResponse<User>>(
        `/user/list?page=${page}&limit=${limit}&search=${searchTerm}&filter=${userTypeFilter}&duplicatePhone=${duplicatePhoneFilter}&duplicateEmail=${duplicateEmailFilter}`,
      );
      // console.log('Success to fetch users:', res);
      setUsers(res.data);
      setTotalItems(res.metadata.totalItems);
      setLastSyncTime(dayjs());
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    limit,
    searchTerm,
    userTypeFilter,
    duplicatePhoneFilter,
    duplicateEmailFilter,
  ]);

  // Hooks
  useEffect(() => {
    fetchUsers();
  }, [page, limit, userTypeFilter, duplicatePhoneFilter, duplicateEmailFilter]);

  // ==============================
  // Update 09/06/2025: Fetch users every 3 seconds
  // ==============================
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUsers(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [fetchUsers]);

  // Handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value;
    setUserTypeFilter(newValue);
    setPage(1);
    // notifyActionSuccess('Filter');
  };

  const handleToggleDuplicatePhone = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDuplicatePhoneFilter(event.target.checked);
    setPage(1);
  };

  const handleToggleDuplicateEmail = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDuplicateEmailFilter(event.target.checked);
    setPage(1);
  };

  // ==============================
  // Update 09/06/2025: Clear all filters
  // ==============================
  const clearFilters = () => {
    setSearchTerm('');
    setUserTypeFilter('All');
    setDuplicatePhoneFilter(false);
    setDuplicateEmailFilter(false);
    setSelectedUser(null);
    setPage(1);
    setLimit(10);
  };

  const handleCloseUserInfoDialog = () => {
    setSelectedUser(null);
    setOpenUserInfoDialog(false);
  };

  const handleDeleteUser = async (user: User) => {
    try {
      const res = await apiDelete<SuccessResponse>(
        `/user/delete/${user.id}?force=${false}`,
      );
      notifyAPIClient(res.message);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleDownloadUsers = async () => {
    if (totalItems === 0) return;

    setDownloadLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: totalItems.toString(),
        search: searchTerm,
        filter: userTypeFilter,
        duplicatePhone: String(duplicatePhoneFilter),
        duplicateEmail: String(duplicateEmailFilter),
      });
      const response = await apiGet<PaginatedResponse<User>>(
        `/user/list?${params.toString()}`,
      );

      const rows = response.data.map((user) => ({
        ID: user.id,
        Avatar: user.pictureUrl,
        'First Name': user.firstName,
        'Last Name': user.lastName,
        'Preferred Name': user.preferredName || 'None',
        'Business Name':
          user.userType !== 'BUSINESS' ? '---' : user.businessName || 'None',
        'Date of Birth':
          user.userType === 'BUSINESS'
            ? '---'
            : new Date(user.dob).toLocaleDateString(),
        Gender: user.gender || 'UNDEFINED',
        Email: user.email || 'None',
        Phone: user.phone || '(+84) ---',
        Parent: user.parent || 'None',
        'User Type': user.userType || 'CHILD/ADULT',
        'Created At': new Date(user.createdAt).toLocaleString(),
      }));

      exportRowsToXlsx(
        rows,
        `users_${dayjs().format('YYYY-MM-DD')}.xlsx`,
        Date.now().toString(),
      );
    } catch (error) {
      console.error('Failed to download users:', error);
      notifyAPIClient('Failed to download users. Please try again later.');
    } finally {
      setDownloadLoading(false);
    }
  };

  // const handleRequestSort = (property: keyof User) => {
  //   const isAsc = orderBy === property && order === "asc";
  //   setOrder(isAsc ? "desc" : "asc");
  //   setOrderBy(property);
  // };

  const emptyRows = limit - users.length;

  return {
    users,
    page,
    limit,
    totalItems,
    searchTerm,
    userTypeFilter,
    duplicatePhoneFilter,
    duplicateEmailFilter,
    emptyRows,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    handleFilterChange,
    handleToggleDuplicatePhone,
    handleToggleDuplicateEmail,
    clearFilters,
    fetchUsers,
    handleDownloadUsers,
    downloadLoading,
    loading,
    setPage,

    // User Info Dialog
    openUserInfoDialog,
    setOpenUserInfoDialog,
    selectedUser,
    setSelectedUser,
    handleCloseUserInfoDialog,

    // Delete User Dialog
    handleDeleteUser,
    openDeleteUserDialog,
    setOpenDeleteUserDialog,

    lastSyncTime,
    highlightedRow,
    setHighlightedRow,
  };
};
